import { Controller } from "@hotwired/stimulus"

// The controller and upload queue survive result-frame navigation. Poll only
// the bounded visible page, and never replace a viewer's active image set.
export default class extends Controller {
  static targets = ["frame", "total", "notice", "previewNotice", "refresh"]
  static values = { refreshUrl: String, statusUrl: String }

  connect() {
    this.connected = true
    this.galleryOpen = false
    this.accessDenied = false
    this.pollDelay = 2000
    this.pollDeadline = Date.now() + 120000
    this.schedulePoll()
  }

  disconnect() {
    this.connected = false
    this.stopPolling()
  }

  get query() {
    try { return JSON.parse(this.frameTarget.querySelector("[data-document-explorer-state]").dataset.explorerParams) }
    catch (_) { return {} }
  }

  recordLayoutIntent(event) {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const link = event.target.closest("[data-document-layout-link]")
    if (!link || !this.element.contains(link)) return
    this.pendingLayout = { href: new URL(link.href, window.location.origin).href, view: link.dataset.documentLayoutLink }
  }

  optimisticallySwitchLayout(event) {
    if (event.target !== this.frameTarget) return
    const intent = this.pendingLayout
    if (!intent || new URL(event.detail.url, window.location.origin).href !== intent.href) return
    this.pendingLayout = null
    const view = intent.view
    const state = this.frameTarget.querySelector("[data-document-explorer-state]")
    this.optimisticLayout = {
      layout: this.frameTarget.querySelector("[data-document-layout]").dataset.documentLayout,
      params: state.dataset.explorerParams
    }

    // Turbo clones the old frame for history before this public fetch event.
    // Keep the responsive layout change here so the outgoing snapshot stays canonical.
    this.frameTarget.querySelector("[data-document-layout]").dataset.documentLayout = view
    this.frameTarget.querySelectorAll("[data-document-layout-link]").forEach((link) => {
      if (link.dataset.documentLayoutLink === view) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    })
    this.frameTarget.querySelectorAll("[data-document-layout-input]").forEach((input) => { input.value = view })
    state.dataset.explorerParams = JSON.stringify({ ...this.query, view })
  }

  restoreOptimisticLayout(event) {
    if (event.target !== this.frameTarget || !this.optimisticLayout) return
    const { layout, params } = this.optimisticLayout
    this.frameTarget.querySelector("[data-document-layout]").dataset.documentLayout = layout
    this.frameTarget.querySelectorAll("[data-document-layout-link]").forEach((link) => {
      if (link.dataset.documentLayoutLink === layout) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    })
    this.frameTarget.querySelectorAll("[data-document-layout-input]").forEach((input) => { input.value = layout })
    this.frameTarget.querySelector("[data-document-explorer-state]").dataset.explorerParams = params
    this.optimisticLayout = null
  }

  frameLoaded(event) {
    if (event.target !== this.frameTarget) return
    const state = this.frameTarget.querySelector("[data-document-explorer-state]")
    this.totalTarget.textContent = `(${state.dataset.totalCount})`
    if (!this.pollRefresh) {
      this.pollDeadline = Date.now() + 120000
      this.pollDelay = 2000
    }
    this.pollRefresh = false
    this.optimisticLayout = null
    this.schedulePoll()
    if (this.deferredRefresh && !this.galleryOpen) {
      this.deferredRefresh = false
      this.refreshFrame()
    }
    if (this.focusAfterMutation) {
      this.frameTarget.querySelector("#document-results-heading")?.focus({ preventScroll: true })
      this.focusAfterMutation = false
    }
  }

  submitted(event) {
    if (!event.detail.success || event.target.querySelector('[name="_method"]')?.value !== "delete") return
    this.focusAfterMutation = true
    // turbo:submit-end can run after the result frame has rendered.
    this.frameTarget.querySelector("#document-results-heading")?.focus({ preventScroll: true })
  }

  uploadsComplete(event) {
    const { savedCount, failedCount } = event.detail
    if (!savedCount) return
    const query = this.query
    const filtered = query.q || query.category || query.file_type
    this.noticeTarget.textContent = `${savedCount} ${savedCount === 1 ? "file" : "files"} uploaded.${failedCount ? ` ${failedCount} ${failedCount === 1 ? "file needs" : "files need"} another try below Add files.` : ""}`
    if (filtered) {
      this.noticeTarget.append(document.createTextNode(" Active filters may hide newly uploaded files. "))
      const clear = document.createElement("a")
      clear.textContent = "Show all documents"
      const url = new URL(window.location.href)
      url.search = new URLSearchParams({ view: query.view || "grid" }).toString()
      clear.href = url.href
      clear.className = "text-clay-700 underline rg-focus"
      clear.dataset.turboFrame = "claim_documents"
      clear.dataset.turboAction = "advance"
      this.noticeTarget.append(clear)
    }
    this.noticeTarget.hidden = false
    this.refreshFrame()
  }

  refreshFrame({ polling = false } = {}) {
    if (!this.connected) return
    if (this.galleryOpen || this.frameTarget.hasAttribute("busy")) { this.deferredRefresh = true; return }
    this.pollRefresh = polling
    const url = new URL(this.refreshUrlValue, window.location.origin)
    url.search = new URLSearchParams(this.query).toString()
    if (this.frameTarget.src === url.href) this.frameTarget.reload()
    else this.frameTarget.src = url.href
  }

  pendingItems() {
    return Array.from(this.frameTarget.querySelectorAll('[data-preview-state="pending"], [data-preview-state="processing"]'))
  }

  schedulePoll() {
    this.stopPolling()
    if (!this.connected || this.accessDenied || document.hidden || this.galleryOpen || this.pendingItems().length === 0) return
    if (Date.now() >= this.pollDeadline) {
      this.showPreviewNotice("Some previews are still preparing. You can refresh them when you are ready.")
      return
    }
    this.pollTimer = window.setTimeout(() => this.poll(), Math.min(this.pollDelay, this.pollDeadline - Date.now()))
  }

  async poll() {
    const items = this.pendingItems().slice(0, 48)
    if (!this.connected || this.accessDenied || !items.length || document.hidden || this.galleryOpen) return
    const remaining = this.pollDeadline - Date.now()
    if (remaining <= 0) { this.schedulePoll(); return }
    const url = new URL(this.statusUrlValue, window.location.origin)
    items.forEach((item) => url.searchParams.append("ids[]", item.dataset.documentId))
    const request = new AbortController()
    this.statusRequest = request
    let timedOut = false
    // Keep the same deadline through both response headers and JSON body reads.
    const timeout = window.setTimeout(() => {
      timedOut = true
      request.abort()
    }, Math.min(15000, remaining))
    this.statusTimeout = timeout
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", signal: request.signal })
      if (!this.connected || this.statusRequest !== request || request.signal.aborted) return
      if (response.status === 401 || response.status === 403) {
        this.accessDenied = true
        this.showPreviewNotice("Your access changed. Sign in again to continue viewing previews.", false)
        return
      }
      if (!response.ok) throw new Error("Preview status unavailable")
      const data = await response.json()
      if (!this.connected || this.statusRequest !== request || request.signal.aborted) return
      const states = new Map(items.map((item) => [item.dataset.documentId, item.dataset.previewState]))
      if (data.documents.some((item) => states.get(item.id) !== item.state)) {
        this.refreshFrame({ polling: true })
        return
      }
      this.pollDelay = Math.min(this.pollDelay * 2, 30000)
      this.schedulePoll()
    } catch (error) {
      if (this.connected && this.statusRequest === request && (timedOut || error.name !== "AbortError")) {
        this.showPreviewNotice(timedOut ? "Preview status took too long. Refresh previews to try again." : "Preview status could not be refreshed. Your documents are still available.")
      }
    } finally {
      window.clearTimeout(timeout)
      if (this.statusTimeout === timeout) this.statusTimeout = null
      if (this.statusRequest === request) this.statusRequest = null
    }
  }

  imageFailed(event) {
    const image = event.target
    const item = image.closest("[data-document-item]")
    if (!item) return
    image.hidden = true
    const link = image.closest("[data-document-gallery-item]")
    if (link) {
      link.removeAttribute("data-document-gallery-item")
      link.removeAttribute("href")
      link.removeAttribute("aria-label")
      link.setAttribute("aria-disabled", "true")
    }
    const label = item.querySelector("[data-document-preview-label]")
    label.textContent = "Preview unavailable"
    label.hidden = false
    this.showPreviewNotice("A preview could not be loaded. Refresh previews to try again.")
  }

  showPreviewNotice(message, allowRefresh = true) {
    this.previewNoticeTarget.textContent = message
    this.previewNoticeTarget.hidden = false
    this.refreshTarget.hidden = !allowRefresh
  }

  manualRefresh() {
    this.pollDeadline = Date.now() + 120000
    this.pollDelay = 2000
    this.refreshFrame()
  }

  stopPolling() {
    window.clearTimeout(this.pollTimer)
    window.clearTimeout(this.statusTimeout)
    this.statusTimeout = null
    this.statusRequest?.abort()
    this.statusRequest = null
  }

  beforeFrameRender(event) {
    if (event.target !== this.frameTarget || !this.galleryOpen) return
    event.preventDefault()
    this.resumeFrame = event.detail.resume
  }

  galleryAccessDenied() { this.accessDenied = true; this.stopPolling() }
  galleryOpened() { this.galleryOpen = true; this.stopPolling() }
  galleryClosed() {
    this.galleryOpen = false
    if (this.resumeFrame) {
      const resume = this.resumeFrame
      this.resumeFrame = null
      resume()
      return
    }
    if (this.deferredRefresh) { this.deferredRefresh = false; this.refreshFrame({ polling: true }) }
    else this.schedulePoll()
  }
  visibilityChanged() { if (document.hidden) this.stopPolling(); else this.schedulePoll() }
  beforeCache() { this.stopPolling() }
}
